import { Injectable } from '@angular/core';
import { Observable, of, from, forkJoin, map, switchMap, mergeMap, toArray, catchError, take, tap, firstValueFrom } from 'rxjs';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { toGoApiError, isGoNotFound, goErrorMessages } from './go-api-error';
import { ApolloClientService } from '../api/apollo-client.service';
import { MigrationConfigService, MigrationEntity } from './migration-config.service';
import { DiffLoggerService } from './diff-logger.service';
import { GoLambdaMapperService, GoLambdaModel, GoLambdaPage } from './go-lambda-mapper.service';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { PaginatedItemsResponseDto } from 'src/app/models/dto/paginatedItemsResponseDto';
import {
  FDD_GET_TEST_CASE_QUERY, FDD_GET_ALL_TEST_CASES_QUERY,
  FDD_GET_TEST_SUITE_QUERY, FDD_GET_ALL_TEST_SUITES_QUERY,
  FDD_GET_TEST_ANALYSIS_QUERY, FDD_GET_ALL_TEST_ANALYSES_QUERY,
  FDD_GET_TEST_SPECIFICATION_QUERY, FDD_GET_ALL_TEST_SPECIFICATIONS_QUERY,
  FDD_SEARCH_TEST_CASES_QUERY, FDD_SEARCH_TEST_SUITES_QUERY, FDD_SEARCH_TEST_ANALYSES_QUERY,
  FDD_SEARCH_TEST_SPECIFICATIONS_QUERY,
  FDD_GET_EESE_COVERAGE_QUERY, FDD_GET_ALL_EESE_COVERAGES_QUERY, FDD_SEARCH_EESE_COVERAGES_QUERY,
  FDD_GET_SW_VERSION_COVERAGE_QUERY, FDD_GET_ALL_SW_VERSION_COVERAGES_QUERY, FDD_SEARCH_SW_VERSION_COVERAGES_QUERY,
  FDD_GET_SPEC_COVERAGE_QUERY, FDD_GET_ALL_SPEC_COVERAGES_QUERY, FDD_SEARCH_SPEC_COVERAGES_QUERY
} from 'src/app/graphQL/queries/fenix-domain-data-queries';
import {
  FDD_CREATE_TEST_CASE_MUTATION, FDD_UPDATE_TEST_CASE_MUTATION, FDD_DELETE_TEST_CASE_MUTATION,
  FDD_CREATE_TEST_SUITE_MUTATION, FDD_UPDATE_TEST_SUITE_MUTATION, FDD_DELETE_TEST_SUITE_MUTATION,
  FDD_CREATE_TEST_ANALYSIS_MUTATION, FDD_UPDATE_TEST_ANALYSIS_MUTATION, FDD_DELETE_TEST_ANALYSIS_MUTATION,
  FDD_CREATE_TEST_SPECIFICATION_MUTATION, FDD_UPDATE_TEST_SPECIFICATION_MUTATION, FDD_DELETE_TEST_SPECIFICATION_MUTATION,
  FDD_CREATE_EESE_COVERAGE_MUTATION, FDD_UPDATE_EESE_COVERAGE_MUTATION, FDD_DELETE_EESE_COVERAGE_MUTATION,
  FDD_CREATE_SW_VERSION_COVERAGE_MUTATION, FDD_UPDATE_SW_VERSION_COVERAGE_MUTATION, FDD_DELETE_SW_VERSION_COVERAGE_MUTATION,
  FDD_CREATE_SPEC_COVERAGE_MUTATION, FDD_UPDATE_SPEC_COVERAGE_MUTATION, FDD_DELETE_SPEC_COVERAGE_MUTATION
} from 'src/app/graphQL/mutations/fenix-domain-data-mutations';

// How many Go create mutations a bulk import runs at once. The Go lambdas have no batch
// mutation, so an import fans out over per-item creates; this keeps a large sheet from
// opening hundreds of concurrent AppSync requests while still beating a serial loop.
const GO_IMPORT_CONCURRENCY = 5;

// Mirrors the Scope enum on the Go lambdas (testcase/testsuite/testanalysis schema.graphql).
// Server resolves MINE/MY_GROUP from caller identity — the client just declares intent.
export type Scope = 'ALL' | 'MINE' | 'MY_GROUP';

const GET_BY_UID_QUERIES: Record<MigrationEntity, any> = {
  testCases: FDD_GET_TEST_CASE_QUERY,
  testSuites: FDD_GET_TEST_SUITE_QUERY,
  testAnalyses: FDD_GET_TEST_ANALYSIS_QUERY,
  testSpecifications: FDD_GET_TEST_SPECIFICATION_QUERY,
  eeseCoverage: FDD_GET_EESE_COVERAGE_QUERY,
  softwareVersionCoverage: FDD_GET_SW_VERSION_COVERAGE_QUERY,
  specificationCoverage: FDD_GET_SPEC_COVERAGE_QUERY,
};

const GET_ALL_QUERIES: Record<MigrationEntity, any> = {
  testCases: FDD_GET_ALL_TEST_CASES_QUERY,
  testSuites: FDD_GET_ALL_TEST_SUITES_QUERY,
  testAnalyses: FDD_GET_ALL_TEST_ANALYSES_QUERY,
  testSpecifications: FDD_GET_ALL_TEST_SPECIFICATIONS_QUERY,
  eeseCoverage: FDD_GET_ALL_EESE_COVERAGES_QUERY,
  softwareVersionCoverage: FDD_GET_ALL_SW_VERSION_COVERAGES_QUERY,
  specificationCoverage: FDD_GET_ALL_SPEC_COVERAGES_QUERY,
};

const GET_BY_UID_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'getTestCase',
  testSuites: 'getTestSuite',
  testAnalyses: 'getTestAnalysis',
  testSpecifications: 'getTestSpecification',
  eeseCoverage: 'getEeseCoverage',
  softwareVersionCoverage: 'getSoftwareVersionCoverage',
  specificationCoverage: 'getSpecificationCoverage',
};

const GET_ALL_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'getAllTestCases',
  testSuites: 'getAllTestSuites',
  testAnalyses: 'getAllTestAnalyses',
  testSpecifications: 'getAllTestSpecifications',
  eeseCoverage: 'getAllEeseCoverages',
  softwareVersionCoverage: 'getAllSoftwareVersionCoverages',
  specificationCoverage: 'getAllSpecificationCoverages',
};

const CREATE_MUTATIONS: Record<MigrationEntity, any> = {
  testCases: FDD_CREATE_TEST_CASE_MUTATION,
  testSuites: FDD_CREATE_TEST_SUITE_MUTATION,
  testAnalyses: FDD_CREATE_TEST_ANALYSIS_MUTATION,
  testSpecifications: FDD_CREATE_TEST_SPECIFICATION_MUTATION,
  eeseCoverage: FDD_CREATE_EESE_COVERAGE_MUTATION,
  softwareVersionCoverage: FDD_CREATE_SW_VERSION_COVERAGE_MUTATION,
  specificationCoverage: FDD_CREATE_SPEC_COVERAGE_MUTATION,
};

const UPDATE_MUTATIONS: Record<MigrationEntity, any> = {
  testCases: FDD_UPDATE_TEST_CASE_MUTATION,
  testSuites: FDD_UPDATE_TEST_SUITE_MUTATION,
  testAnalyses: FDD_UPDATE_TEST_ANALYSIS_MUTATION,
  testSpecifications: FDD_UPDATE_TEST_SPECIFICATION_MUTATION,
  eeseCoverage: FDD_UPDATE_EESE_COVERAGE_MUTATION,
  softwareVersionCoverage: FDD_UPDATE_SW_VERSION_COVERAGE_MUTATION,
  specificationCoverage: FDD_UPDATE_SPEC_COVERAGE_MUTATION,
};

const DELETE_MUTATIONS: Record<MigrationEntity, any> = {
  testCases: FDD_DELETE_TEST_CASE_MUTATION,
  testSuites: FDD_DELETE_TEST_SUITE_MUTATION,
  testAnalyses: FDD_DELETE_TEST_ANALYSIS_MUTATION,
  testSpecifications: FDD_DELETE_TEST_SPECIFICATION_MUTATION,
  eeseCoverage: FDD_DELETE_EESE_COVERAGE_MUTATION,
  softwareVersionCoverage: FDD_DELETE_SW_VERSION_COVERAGE_MUTATION,
  specificationCoverage: FDD_DELETE_SPEC_COVERAGE_MUTATION,
};

const CREATE_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'createTestCase',
  testSuites: 'createTestSuite',
  testAnalyses: 'createTestAnalysis',
  testSpecifications: 'createTestSpecification',
  eeseCoverage: 'createEeseCoverage',
  softwareVersionCoverage: 'createSoftwareVersionCoverage',
  specificationCoverage: 'createSpecificationCoverage',
};

const UPDATE_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'updateTestCase',
  testSuites: 'updateTestSuite',
  testAnalyses: 'updateTestAnalysis',
  testSpecifications: 'updateTestSpecification',
  eeseCoverage: 'updateEeseCoverage',
  softwareVersionCoverage: 'updateSoftwareVersionCoverage',
  specificationCoverage: 'updateSpecificationCoverage',
};

const DELETE_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'deleteTestCase',
  testSuites: 'deleteTestSuite',
  testAnalyses: 'deleteTestAnalysis',
  testSpecifications: 'deleteTestSpecification',
  eeseCoverage: 'deleteEeseCoverage',
  softwareVersionCoverage: 'deleteSoftwareVersionCoverage',
  specificationCoverage: 'deleteSpecificationCoverage',
};

const SEARCH_QUERIES: Record<MigrationEntity, any> = {
  testCases: FDD_SEARCH_TEST_CASES_QUERY,
  testSuites: FDD_SEARCH_TEST_SUITES_QUERY,
  testAnalyses: FDD_SEARCH_TEST_ANALYSES_QUERY,
  testSpecifications: FDD_SEARCH_TEST_SPECIFICATIONS_QUERY,
  eeseCoverage: FDD_SEARCH_EESE_COVERAGES_QUERY,
  softwareVersionCoverage: FDD_SEARCH_SW_VERSION_COVERAGES_QUERY,
  specificationCoverage: FDD_SEARCH_SPEC_COVERAGES_QUERY,
};

const SEARCH_FIELDS: Record<MigrationEntity, string> = {
  testCases: 'searchTestCases',
  testSuites: 'searchTestSuites',
  testAnalyses: 'searchTestAnalyses',
  testSpecifications: 'searchTestSpecifications',
  eeseCoverage: 'searchEeseCoverages',
  softwareVersionCoverage: 'searchSoftwareVersionCoverages',
  specificationCoverage: 'searchSpecificationCoverages',
};

const SEARCH_ARG_NAMES: Record<MigrationEntity, string> = {
  testCases: 'testCase',
  testSuites: 'testSuite',
  testAnalyses: 'testAnalysis',
  testSpecifications: 'testSpecification',
  eeseCoverage: 'eeseCoverage',
  softwareVersionCoverage: 'softwareVersionCoverage',
  specificationCoverage: 'specificationCoverage',
};

/**
 * Dual API service for migration.
 *
 * Behaviour is driven by `MigrationMode` (see `MigrationConfigService`):
 *
 *  - `old`  — only the old C# API is called (zero overhead).
 *  - `new`  — only the Go Lambda API is called; the C# API is not contacted.
 *             Reads return the new API's own failure shape (an unsuccessful `ResponseDto`,
 *             or an empty list) rather than falling back to C# — in this mode Go is the
 *             sole source of truth, and quietly serving data from the API we are migrating
 *             off would hide the failure.
 *             Writes report failure through the same unsuccessful `ResponseDto` the
 *             `both` + `displaySource: 'new'` path already uses, which the item pages
 *             render as a sticky error toast.
 *  - `both` — every read calls BOTH APIs in parallel; responses are compared and
 *             differences logged, and `displaySource` controls which one reaches the UI.
 *             Every mutation is sent to both backends; the old API result is the primary
 *             response unless `displaySource === 'new'`.
 */
@Injectable({
  providedIn: 'root'
})
export class DualApiService {

  constructor(
    private apolloClientService: ApolloClientService,
    private migrationConfig: MigrationConfigService,
    private diffLogger: DiffLoggerService,
    private mapper: GoLambdaMapperService
  ) {}

  // ──────────────────────────────────────────
  // DUAL READ: getByUid
  // ──────────────────────────────────────────

  /**
   * Call both APIs for getByUid, compare, return based on displaySource.
   * @param entity Which entity type
   * @param uid Entity UID
   * @param oldApiCall$ The existing Observable from the old API service
   * @returns Observable<ResponseDto> from whichever source is configured
   */
  dualGetByUid(entity: MigrationEntity, uid: string, oldApiCall$: Observable<ResponseDto>): Observable<ResponseDto> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.getByUid: mode=old, using old API only`);
          return oldApiCall$;
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.getByUid: mode=new, using Go API only`);
          return this.goLambdaGetByUid(entity, uid).pipe(
            catchError(err => {
              console.error(`[Migration] ${entity}.getByUid NEW API error (new-only mode):`, err.message);
              return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto);
            })
          );
        }

        console.log(`[Migration] ${entity}.getByUid: mode=both, calling both APIs (display=${config.displaySource})`);
        const newApiCall$ = this.goLambdaGetByUid(entity, uid);

        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.getByUid OLD API error:`, err.message); return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto); })),
          new: newApiCall$.pipe(catchError(err => { console.error(`[Migration] ${entity}.getByUid NEW API error:`, err.message); return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto); }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.getByUid: old success=${results.old?.success}, new success=${results.new?.success}`);
            this.diffLogger.compare(entity, 'getByUid', results.old?.returnValue, results.new?.returnValue, uid);
            return config.displaySource === 'new' ? results.new : results.old;
          })
        );
      })
    );
  }

  // ──────────────────────────────────────────
  // DUAL READ: getAll / paginated
  // ──────────────────────────────────────────

  /**
   * Call both APIs for paginated list, compare, return based on displaySource.
   * @param entity Which entity type
   * @param currentPage Page number for Go Lambda API
   * @param pageSize Page size for Go Lambda API
   * @param filters Filters for Go Lambda API (field/op/value)
   * @param oldApiCall$ The existing Observable from the old API service
   * @param scope Optional Scope. Server resolves MINE/MY_GROUP from caller identity.
   * @returns Observable<PaginatedItemsResponseDto>
   */
  dualGetAllPaginated(entity: MigrationEntity, currentPage: number, pageSize: number, filters: { field: string, op: string, value: string }[], oldApiCall$: Observable<PaginatedItemsResponseDto>, scope?: Scope): Observable<PaginatedItemsResponseDto> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.getAllPaginated: mode=old, using old API only`);
          return oldApiCall$;
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.getAllPaginated: mode=new, using Go API only (page=${currentPage}, scope=${scope ?? 'ALL'})`);
          // A Go failure propagates as an error. It used to resolve to an empty page, which the
          // list stores cached as a legitimate "no items" result and showed without any message.
          return this.goLambdaGetAll(entity, currentPage, pageSize, filters, scope).pipe(
            catchError(err => {
              console.error(`[Migration] ${entity}.getAllPaginated NEW API error (new-only mode):`, err.message);
              throw err;
            })
          );
        }

        console.log(`[Migration] ${entity}.getAllPaginated: mode=both, calling both APIs (page=${currentPage}, display=${config.displaySource}, scope=${scope ?? 'ALL'})`);
        const newApiCall$ = this.goLambdaGetAll(entity, currentPage, pageSize, filters, scope);
        let newError: Error | undefined;

        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.getAllPaginated OLD API error:`, err.message); return of({ totalCount: 0, items: [] } as PaginatedItemsResponseDto); })),
          new: newApiCall$.pipe(catchError(err => { newError = err; console.error(`[Migration] ${entity}.getAllPaginated NEW API error:`, err.message); return of({ totalCount: 0, items: [] } as PaginatedItemsResponseDto); }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.getAllPaginated: old count=${results.old?.totalCount}, new count=${results.new?.totalCount}`);
            this.diffLogger.compare(entity, 'getAll', results.old?.items, results.new?.items);
            // Only the displayed source decides success: a failed Go call is still compared, but it
            // must not be rendered (and cached) as an empty list when Go is what the user sees.
            if (config.displaySource === 'new' && newError) throw newError;
            return config.displaySource === 'new' ? results.new : results.old;
          })
        );
      })
    );
  }

  /**
   * Call both APIs for non-paginated list (old API has no pagination).
   */
  dualGetAll(entity: MigrationEntity, filters: { field: string, op: string, value: string }[], oldApiCall$: Observable<BaseTestItemDto[]>, scope?: Scope): Observable<BaseTestItemDto[]> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.getAll: mode=old, using old API only`);
          return oldApiCall$;
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.getAll: mode=new, using Go API only (scope=${scope ?? 'ALL'})`);
          const tNew = performance.now();
          // Go Lambda requires pagination — use large page to get all
          // A Go failure propagates as an error (see dualGetAllPaginated).
          return this.goLambdaGetAll(entity, 1, 100000, filters, scope).pipe(
            tap(() => console.log(`[Perf] ${entity}.getAll NEW resolved: ${(performance.now() - tNew).toFixed(0)}ms`)),
            map(result => result.items),
            catchError(err => {
              console.error(`[Migration] ${entity}.getAll NEW API error (new-only mode):`, err.message);
              throw err;
            })
          );
        }

        console.log(`[Migration] ${entity}.getAll: mode=both, calling both APIs (display=${config.displaySource}, scope=${scope ?? 'ALL'})`);
        const t0 = performance.now();
        // Go Lambda requires pagination — use large page to get all
        const newApiCall$ = this.goLambdaGetAll(entity, 1, 100000, filters, scope).pipe(
          tap(() => console.log(`[Perf] ${entity}.getAll NEW resolved: ${(performance.now() - t0).toFixed(0)}ms`)),
          map(result => result.items)
        );
        let newError: Error | undefined;

        return forkJoin({
          old: oldApiCall$.pipe(take(1), tap(r => console.log(`[Perf] ${entity}.getAll OLD resolved: ${(performance.now() - t0).toFixed(0)}ms, ${r?.length ?? 0} items`)), catchError(err => { console.error(`[Migration] ${entity}.getAll OLD API error:`, err.message); return of([] as BaseTestItemDto[]); })),
          new: newApiCall$.pipe(catchError(err => { newError = err; console.error(`[Migration] ${entity}.getAll NEW API error:`, err.message); return of([] as BaseTestItemDto[]); }))
        }).pipe(
          map(results => {
            console.log(`[Perf] ${entity}.getAll forkJoin TOTAL: ${(performance.now() - t0).toFixed(0)}ms (old=${results.old?.length ?? 0}, new=${results.new?.length ?? 0})`);
            const tCmp = performance.now();
            this.diffLogger.compare(entity, 'getAll', results.old, results.new);
            console.log(`[Perf] ${entity}.getAll compare: ${(performance.now() - tCmp).toFixed(0)}ms`);
            if (config.displaySource === 'new' && newError) throw newError;
            return config.displaySource === 'new' ? results.new : results.old;
          })
        );
      })
    );
  }

  // ──────────────────────────────────────────
  // ──────────────────────────────────────────
  // DUAL READ: search
  // ──────────────────────────────────────────

  // scope: the lambdas resolve MINE / MY_GROUP from the caller, like getAll (undefined = ALL).
  dualSearch(entity: MigrationEntity, dto: any, oldApiCall$: Observable<BaseTestItemDto[]>, scope?: Scope): Observable<BaseTestItemDto[]> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.search: mode=old, using old API only`);
          return oldApiCall$;
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.search: mode=new, using Go API only (scope=${scope ?? 'ALL'})`);
          // A Go failure propagates as an error (see dualGetAllPaginated).
          return this.goLambdaSearch(entity, this.mapper.fromDtoToSearchData(dto, entity, scope), scope).pipe(
            catchError(err => {
              console.error(`[Migration] ${entity}.search NEW API error (new-only mode):`, err.message);
              throw err;
            })
          );
        }

        console.log(`[Migration] ${entity}.search: mode=both, calling both APIs (display=${config.displaySource}, scope=${scope ?? 'ALL'})`);
        const searchData = this.mapper.fromDtoToSearchData(dto, entity, scope);
        const newApiCall$ = this.goLambdaSearch(entity, searchData, scope);
        let newError: Error | undefined;

        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.search OLD API error:`, err.message); return of([] as BaseTestItemDto[]); })),
          new: newApiCall$.pipe(catchError(err => { newError = err; console.error(`[Migration] ${entity}.search NEW API error:`, err.message); return of([] as BaseTestItemDto[]); }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.search: old count=${results.old?.length}, new count=${results.new?.length}`);
            this.diffLogger.compare(entity, 'search', results.old, results.new);
            if (config.displaySource === 'new' && newError) throw newError;
            return config.displaySource === 'new' ? results.new : results.old;
          })
        );
      })
    );
  }

  // ──────────────────────────────────────────
  // DUAL WRITE: create / update / delete
  // ──────────────────────────────────────────

  /**
   * Call both APIs for create. Old API is called first, then new API.
   * Returns the old API response.
   */
  dualCreate(entity: MigrationEntity, dto: any, oldApiCall$: Observable<ResponseDto>): Observable<ResponseDto> {
    console.log(`[Migration] ${entity}.create: dualCreate called`);
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.create: mode=old, using old API only`);
          // take(1): oldApiCall$ is built on apolloClient$, which never completes on its own
          // (it tracks live auth/flag state) - without this, callers awaiting completion
          // (e.g. dispatch().subscribe(...) for post-save navigation) would hang forever.
          return oldApiCall$.pipe(take(1));
        }

        if (config.mode === 'new') {
          const newData = this.mapper.fromDtoToGoData(dto, entity);
          console.log(`[Migration] ${entity}.create: mode=new, using Go API only, data=`, newData.substring(0, 300));
          return this.goLambdaMutate(CREATE_MUTATIONS[entity], CREATE_FIELDS[entity], { data: newData }).pipe(
            map(returnValue => ({ success: true, returnValue, responseMessages: [] }) as ResponseDto),
            catchError(err => {
              this.diffLogger.logWriteFailure(entity, 'create', err);
              return of({ success: false, returnValue: null, responseMessages: goErrorMessages(err, 'Failed to save to new API') } as ResponseDto);
            })
          );
        }

        const data = this.mapper.fromDtoToGoData(dto, entity);
        console.log(`[Migration] ${entity}.create: mode=both, calling both APIs, data=`, data.substring(0, 300));
        let newError: unknown = null;
        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.create OLD API error:`, err.message); return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto); })),
          new: this.goLambdaMutate(CREATE_MUTATIONS[entity], CREATE_FIELDS[entity], { data }).pipe(catchError(err => { newError = err; this.diffLogger.logWriteFailure(entity, 'create', err); return of(null); }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.create: old success=${results.old?.success}, new=${results.new != null ? 'success' : 'failed'}, displaySource=${config.displaySource}`);
            // When 'new' is the displayed source it's the source of truth for the UI, so a
            // failed Go write must surface as a failure — not fall back to the old API's success
            // (which would redirect the user onto a save that isn't in the store the list reads).
            if (config.displaySource === 'new') {
              return results.new != null
                ? { success: true, returnValue: results.new, responseMessages: [] } as ResponseDto
                : { success: false, returnValue: null, responseMessages: goErrorMessages(newError, 'Failed to save to new API') } as ResponseDto;
            }
            return results.old;
          })
        );
      })
    );
  }

  /**
   * Call both APIs for a bulk import.
   *
   * The old API has a single batch endpoint (`configuratorImportTestCases`) that
   * validates the whole batch before writing anything; `oldApiCall$` is that call and
   * is left untouched, so `mode: 'old'` behaves exactly as it did before dual write.
   *
   * The Go lambdas have no batch mutation, so the new side fans the batch out over the
   * per-item create mutation with a bounded concurrency. That means the Go side has no
   * all-or-nothing gate — a partial write is possible, which is also true of the old
   * importer once validation passes (it inserts in a loop with no transaction, hence
   * its own IMPORT_HALFWAY_SUCCESSFUL result).
   *
   * @param dtos Items to import, with link names already resolved to backend ids.
   */
  dualImport(entity: MigrationEntity, dtos: any[], oldApiCall$: Observable<ResponseDto>): Observable<ResponseDto> {
    console.log(`[Migration] ${entity}.import: dualImport called with ${dtos.length} item(s)`);
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          console.log(`[Migration] ${entity}.import: mode=old, using old API only`);
          // take(1): see dualCreate - oldApiCall$ never completes on its own.
          return oldApiCall$.pipe(take(1));
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.import: mode=new, using Go API only`);
          return this.goLambdaImport(entity, dtos);
        }

        console.log(`[Migration] ${entity}.import: mode=both, old API first`);
        // Deliberately sequential, unlike dualCreate/dualUpdate which forkJoin.
        // The old importer validates the entire batch before writing anything, and that
        // gate is the only semantic validation either backend has (the Go schema only
        // checks shape). Running the Go fan-out in parallel would drop N rows into
        // Postgres for a sheet the C# importer rejects outright, so the Go writes wait
        // for the old batch to pass. Import is a slow, deliberate action; the lost
        // parallelism costs far less than reconciling that divergence.
        return oldApiCall$.pipe(
          take(1),
          catchError(err => { console.error(`[Migration] ${entity}.import OLD API error:`, err.message); return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto); }),
          switchMap(oldResult => {
            if (!oldResult?.success) {
              console.log(`[Migration] ${entity}.import: old API rejected the batch, skipping Go writes`);
              return of(oldResult);
            }

            return this.goLambdaImport(entity, dtos).pipe(
              map(newResult => {
                console.log(`[Migration] ${entity}.import: old success=true, new success=${newResult?.success}, displaySource=${config.displaySource}`);
                // Same rule as dualCreate: when 'new' is the displayed source it is the
                // source of truth for the UI, so a failed Go write must not be masked.
                return config.displaySource === 'new' ? newResult : oldResult;
              })
            );
          })
        );
      })
    );
  }

  /**
   * Fan a batch out over the Go per-item create mutation.
   *
   * Failures are collected rather than thrown so one bad row cannot abort the rest —
   * matching the old importer's per-item insert loop. Messages mirror the C# importer's
   * wording so `import-step` renders both sources identically (including the uid the
   * "Open test case" button parses out of a success message).
   */
  private goLambdaImport(entity: MigrationEntity, dtos: any[]): Observable<ResponseDto> {
    if (!dtos.length) {
      return of({ success: false, returnValue: [], responseMessages: ['Execution unsuccessful. No Test Cases have been created'] } as ResponseDto);
    }

    return from(dtos.map((dto, index) => ({ dto, index }))).pipe(
      mergeMap(({ dto, index }) => {
        const data = this.mapper.fromDtoToGoData(dto, entity);
        return this.goLambdaMutate(CREATE_MUTATIONS[entity], CREATE_FIELDS[entity], { data }).pipe(
          map(uid => ({ index, uid, name: dto?.name ?? '', error: null as string | null })),
          catchError(err => {
            this.diffLogger.logWriteFailure(entity, 'create', err);
            return of({ index, uid: null as string | null, name: dto?.name ?? '', error: err?.message ?? 'unknown error' });
          })
        );
      }, GO_IMPORT_CONCURRENCY),
      toArray(),
      // mergeMap emits in completion order; restore sheet order so the messages line up
      // with the rows the user saw in the preview.
      map(results => results.sort((a, b) => a.index - b.index)),
      map(results => {
        const created = results.filter(r => r.uid != null);
        const failed = results.filter(r => r.uid == null);

        const messages = results.map(r => r.uid != null
          ? `Test case '${r.name}' has been created and is now assigned to '${r.uid}'`
          : `Test case '${r.name}': failed to create. Error: ${r.error}`);

        if (failed.length === 0) {
          messages.unshift('Execution successful. Test Cases have been created');
        } else if (created.length > 0) {
          messages.unshift('Execution encountered problems. Some Test Cases have been created, but not all');
        } else {
          messages.unshift('Execution unsuccessful. No Test Cases have been created');
        }

        return {
          success: failed.length === 0,
          returnValue: created.map(r => r.uid),
          responseMessages: messages,
        } as ResponseDto;
      })
    );
  }

  /**
   * Call both APIs for update. Old API is called first, then new API.
   */
  dualUpdate(entity: MigrationEntity, uid: string, dto: any, oldApiCall$: Observable<ResponseDto>): Observable<ResponseDto> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          // take(1): see dualCreate - oldApiCall$ never completes on its own.
          return oldApiCall$.pipe(take(1));
        }

        // Strip uid from data — Go Lambda takes uid as a separate argument and rejects it inside data
        const { uid: _uid, ...dtoWithoutUid } = dto;
        const data = this.mapper.fromDtoToGoData(dtoWithoutUid, entity, true);

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.update: mode=new, using Go API only uid=${uid}`);
          return this.goLambdaMutate(UPDATE_MUTATIONS[entity], UPDATE_FIELDS[entity], { uid, data }).pipe(
            map(returnValue => ({ success: true, returnValue, responseMessages: [] }) as ResponseDto),
            catchError(err => {
              if (isGoNotFound(err)) { this.diffLogger.logNotMigrated(entity, 'update', uid); }
              else { this.diffLogger.logWriteFailure(entity, 'update', err); }
              return of({ success: false, returnValue: null, responseMessages: goErrorMessages(err, 'Failed to save to new API') } as ResponseDto);
            })
          );
        }

        console.log(`[Migration] ${entity}.update: mode=both, calling both APIs uid=${uid}`);
        let newError: unknown = null;
        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.update OLD API error:`, err.message); return of({ success: false, returnValue: null, responseMessages: [err.message] } as ResponseDto); })),
          new: this.goLambdaMutate(UPDATE_MUTATIONS[entity], UPDATE_FIELDS[entity], { uid, data }).pipe(catchError(err => {
            newError = err;
            if (isGoNotFound(err)) { this.diffLogger.logNotMigrated(entity, 'update', uid); }
            else { this.diffLogger.logWriteFailure(entity, 'update', err); }
            return of(null);
          }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.update: old success=${results.old?.success}, new=${results.new != null ? 'success' : 'failed'}, displaySource=${config.displaySource}`);
            // 'new' is the source of truth when displayed — surface a failed Go write instead of
            // masking it with the old API's success (see dualCreate).
            if (config.displaySource === 'new') {
              return results.new != null
                ? { success: true, returnValue: results.new, responseMessages: [] } as ResponseDto
                : { success: false, returnValue: null, responseMessages: goErrorMessages(newError, 'Failed to save to new API') } as ResponseDto;
            }
            return results.old;
          })
        );
      })
    );
  }

  /**
   * Call both APIs for delete.
   */
  dualDelete(entity: MigrationEntity, uid: string, oldApiCall$: Observable<any>): Observable<any> {
    return this.migrationConfig.getEntityConfig$(entity).pipe(
      take(1),
      switchMap(config => {
        if (config.mode === 'old') {
          // take(1): see dualCreate - oldApiCall$ never completes on its own.
          return oldApiCall$.pipe(take(1));
        }

        if (config.mode === 'new') {
          console.log(`[Migration] ${entity}.delete: mode=new, using Go API only uid=${uid}`);
          return this.goLambdaMutate(DELETE_MUTATIONS[entity], DELETE_FIELDS[entity], { uid }).pipe(
            catchError(err => {
              if (isGoNotFound(err)) { this.diffLogger.logNotMigrated(entity, 'delete', uid); }
              else { this.diffLogger.logWriteFailure(entity, 'delete', err); }
              // Matches the `both` + display='new' branch: null signals a failed delete.
              return of(null);
            })
          );
        }

        console.log(`[Migration] ${entity}.delete: mode=both, calling both APIs uid=${uid}`);
        return forkJoin({
          old: oldApiCall$.pipe(take(1), catchError(err => { console.error(`[Migration] ${entity}.delete OLD API error:`, err.message); return of(null); })),
          new: this.goLambdaMutate(DELETE_MUTATIONS[entity], DELETE_FIELDS[entity], { uid }).pipe(catchError(err => {
            if (isGoNotFound(err)) { this.diffLogger.logNotMigrated(entity, 'delete', uid); }
            else { this.diffLogger.logWriteFailure(entity, 'delete', err); }
            return of(null);
          }))
        }).pipe(
          map(results => {
            console.log(`[Migration] ${entity}.delete: old=${results.old != null ? 'success' : 'failed'}, new=${results.new != null ? 'success' : 'failed'}, displaySource=${config.displaySource}`);
            // 'new' is the source of truth when displayed — return its (null-on-failure) result
            // instead of masking a failed Go delete with the old API's success.
            if (config.displaySource === 'new') {
              return results.new;
            }
            return results.old;
          })
        );
      })
    );
  }

  // ──────────────────────────────────────────
  // Go Lambda API calls (internal)
  // ──────────────────────────────────────────

  private goLambdaGetByUid(entity: MigrationEntity, uid: string): Observable<ResponseDto> {
    console.log(`[Migration] Go Lambda: ${GET_BY_UID_FIELDS[entity]}(uid=${uid})`);
    return this.apolloClientService.fenixDomainDataClient$.pipe(
      take(1),
      tap(() => console.log(`[Migration] Go Lambda client obtained, sending ${GET_BY_UID_FIELDS[entity]} query`)),
      switchMap(client =>
        client.query<any>({
          query: GET_BY_UID_QUERIES[entity],
          variables: { uid }
        })
      ),
      map(result => {
        // The fenix-domain-data client runs with errorPolicy 'all', so a GraphQL error
        // RESOLVES rather than throwing: the field comes back null and the reason sits in
        // result.error (Apollo Client v4 collapses GraphQL errors into a single
        // CombinedGraphQLErrors). Without this branch the null field falls through to
        // toResponseDto(null), which reports a generic 'Not found' and loses the real
        // message — e.g. the server's view-restriction denial. Surfacing the messages as
        // responseMessages matches the old API's { success, returnValue, responseMessages }
        // shape, which the item pages already render as a sticky error toast.
        if (result.error) {
          const responseMessages = CombinedGraphQLErrors.is(result.error)
            ? result.error.errors.map(e => e.message)
            : [result.error.message];
          console.warn(`[Migration] Go Lambda ${GET_BY_UID_FIELDS[entity]} returned errors:`, responseMessages);
          return { success: false, returnValue: null, responseMessages } as ResponseDto;
        }
        const model: GoLambdaModel = result.data?.[GET_BY_UID_FIELDS[entity]];
        console.log(`[Migration] Go Lambda ${GET_BY_UID_FIELDS[entity]} response:`, model ? 'data received' : 'null');
        return this.mapper.toResponseDto(model);
      })
    );
  }

  private goLambdaGetAll(entity: MigrationEntity, currentPage: number, pageSize: number, filters?: { field: string, op: string, value: string }[], scope?: Scope): Observable<PaginatedItemsResponseDto> {
    console.log(`[Migration] Go Lambda: ${GET_ALL_FIELDS[entity]}(page=${currentPage}, size=${pageSize}, scope=${scope ?? 'ALL'}, filters=${JSON.stringify(filters)})`);
    const field = GET_ALL_FIELDS[entity];
    const tStart = performance.now();
    let tQuery = tStart;
    return this.apolloClientService.fenixDomainDataClient$.pipe(
      take(1),
      tap(() => { tQuery = performance.now(); console.log(`[Migration] Go Lambda client obtained, sending ${field} query`); }),
      switchMap(client =>
        client.query<any>({
          query: GET_ALL_QUERIES[entity],
          variables: { currentPage, pageSize, filters: filters ?? [], scope: scope ?? null }
        })
      ),
      tap(() => console.log(`[Perf] Go ${field} network+parse: ${(performance.now() - tQuery).toFixed(0)}ms`)),
      map(result => {
        // errorPolicy 'all': a GraphQL error resolves with the field null instead of throwing.
        // Without this check the null page became an empty list (see goLambdaGetByUid).
        this.throwIfGraphQLError(result, field);
        const page: GoLambdaPage = result.data[field];
        const items = page?.Items ?? [];
        const perItemBytes = items.length > 0 ? JSON.stringify(items[0]).length : 0;
        console.log(`[Perf] Go ${field}: ${items.length} items, ~${((perItemBytes * items.length) / 1024).toFixed(0)} KB est (${perItemBytes} B/item)`);
        const tMap = performance.now();
        const mapped = this.mapper.toPaginatedDto(page);
        console.log(`[Perf] Go ${field} mapping: ${(performance.now() - tMap).toFixed(0)}ms; goLambdaGetAll total ${(performance.now() - tStart).toFixed(0)}ms`);
        return mapped;
      })
    );
  }

  private goLambdaSearch(entity: MigrationEntity, searchData: string, scope?: Scope): Observable<BaseTestItemDto[]> {
    const argName = SEARCH_ARG_NAMES[entity];
    console.log(`[Migration] Go Lambda: ${SEARCH_FIELDS[entity]}(${argName}=${searchData.substring(0, 200)}, scope=${scope ?? 'ALL'})`);
    return this.apolloClientService.fenixDomainDataClient$.pipe(
      take(1),
      tap(() => console.log(`[Migration] Go Lambda client obtained, sending ${SEARCH_FIELDS[entity]} query`)),
      switchMap(client =>
        client.query<any>({
          query: SEARCH_QUERIES[entity],
          variables: { [argName]: searchData, currentPage: 1, pageSize: 100000, scope: scope ?? null }
        })
      ),
      map(result => {
        this.throwIfGraphQLError(result, SEARCH_FIELDS[entity]);
        const page: GoLambdaPage = result.data[SEARCH_FIELDS[entity]];
        console.log(`[Migration] Go Lambda ${SEARCH_FIELDS[entity]} response: ${page?.TotalCount ?? 0} total, ${page?.Items?.length ?? 0} items`);
        const mapped = this.mapper.toPaginatedDto(page);
        return mapped.items;
      })
    );
  }

  /**
   * Turns an errorPolicy-'all' resolved GraphQL error into a thrown GoApiError carrying the
   * server messages, the errorType code and errorInfo (lambda-conventions.md section 3).
   */
  private throwIfGraphQLError(result: { error?: any }, field: string): void {
    if (!result.error) return;
    const goError = toGoApiError(result.error);
    console.warn(`[Migration] Go Lambda ${field} returned ${goError.code ?? 'an error'}:`, goError.messages, goError.info ?? '');
    throw goError;
  }

  private goLambdaMutate(mutation: any, field: string, variables: any): Observable<any> {
    console.log(`[Migration] Go Lambda mutate: ${field}`, JSON.stringify(variables).substring(0, 500));
    return this.apolloClientService.fenixDomainDataClient$.pipe(
      take(1),
      switchMap(client =>
        client.mutate<any>({ mutation, variables })
      ),
      tap(result => {
        if (result.error) {
          console.error(`[Migration] Go Lambda ${field} GraphQL errors:`, result.error);
        } else {
          console.log(`[Migration] Go Lambda ${field} success:`, result.data[field]);
        }
      }),
      map(result => {
        if (result.error) {
          // GoApiError: messages for the user, code (errorType) to branch on, info (errorInfo).
          throw toGoApiError(result.error);
        }
        return result.data[field];
      })
    );
  }
}
